/**
 * IndexedDB utilities for ensuring consistent database setup across environments
 * Handles the common production issue where database version exists but stores don't
 */

import { conditionalLog, conditionalInfo } from './consoleLogger';

const DB_NAME = 'STBWorkshop';
const DB_VERSION = 23;

// Define all required object stores
const REQUIRED_STORES = [
  'orders',
  'clients', 
  'technicians',
  'equipment_catalog',
  'blacklist_incidents',
  'brands',
  'equipment_types',
  'specifications'
];

/**
 * Opens IndexedDB and ensures all required object stores exist
 * Handles both upgrade scenarios and existing database scenarios
 * @returns {Promise<IDBDatabase>} Opened database with all stores
 */
export const openIndexedDBWithStores = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('Error opening IndexedDB:', request.error);
      reject(request.error);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      console.log(`🔨 Upgrading IndexedDB to version ${db.version}`);
      
      // Create all required stores
      createMissingStores(db);
    };
    
    request.onsuccess = () => {
      const db = request.result;
      
      // Check if all required stores exist
      const missingStores = REQUIRED_STORES.filter(store => 
        !db.objectStoreNames.contains(store)
      );
      
      if (missingStores.length > 0) {
        console.warn(`⚠️ Missing stores detected: ${missingStores.join(', ')}`);
        console.log('🔄 Forcing database recreation to add missing stores...');
        
        db.close();
        
        // Delete and recreate database to trigger onupgradeneeded
        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
        
        deleteRequest.onsuccess = () => {
          console.log('✅ Old database deleted, creating fresh database...');
          
          // Recreate with all stores
          const newRequest = indexedDB.open(DB_NAME, DB_VERSION);
          
          newRequest.onupgradeneeded = (event) => {
            const newDb = event.target.result;
            console.log(`🔨 Creating fresh IndexedDB version ${newDb.version}`);
            createMissingStores(newDb);
          };
          
          newRequest.onsuccess = () => {
            const newDb = newRequest.result;
            console.log(`✅ Fresh database ready with ${newDb.objectStoreNames.length} stores`);
            resolve(newDb);
          };
          
          newRequest.onerror = () => {
            console.error('❌ Error creating fresh database:', newRequest.error);
            reject(newRequest.error);
          };
        };
        
        deleteRequest.onerror = () => {
          console.error('❌ Error deleting old database:', deleteRequest.error);
          reject(deleteRequest.error);
        };
      } else {
        conditionalLog(`✅ All ${REQUIRED_STORES.length} required stores present`);
        resolve(db);
      }
    };
  });
};

/**
 * Creates all missing object stores in the database
 * @param {IDBDatabase} db - Database instance
 */
const createMissingStores = (db) => {
  REQUIRED_STORES.forEach(storeName => {
    if (!db.objectStoreNames.contains(storeName)) {
      console.log(`📦 Creating ${storeName} object store`);
      db.createObjectStore(storeName, { keyPath: 'id' });
    }
  });
  
  console.log(`📊 Database setup complete with ${db.objectStoreNames.length} stores`);
};

/**
 * Simple utility to open IndexedDB with version check only
 * Use this for services that don't need the full store creation logic
 * @returns {Promise<IDBDatabase>}
 */
export const openIndexedDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      createMissingStores(db);
    };
  });
};

/**
 * Initialize IndexedDB at app startup
 * Call this in your main app component to ensure database is ready
 * @returns {Promise<boolean>} Success status
 */
export const initializeIndexedDB = async () => {
  try {
    conditionalLog('🚀 Initializing IndexedDB for STB Workshop...');
    const db = await openIndexedDBWithStores();
    conditionalLog(`✅ IndexedDB ready with ${db.objectStoreNames.length} stores:`, Array.from(db.objectStoreNames));
    db.close();
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize IndexedDB:', error);
    return false;
  }
};